import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { WebStorage } from '../storage/web.storage';
import { LookupService } from '../../../service/lookup.service';
import { StudentDTO } from '../../../model/StudentDTO';
import { StaffDTO } from '../../../model/StaffDTO';
import { GuardianDTO } from '../../../model/GuardianDTO';
import { Common } from '../../../utils/common';
import { HttpResponse } from '@angular/common/http';

@Injectable({
    providedIn: 'root',
})
export class OwnershipGuard {
    constructor(
        private router: Router,
        private storage: WebStorage,
        private lookupService: LookupService
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        const user = this.storage.getUserData();
        if (!user) {
            return this.router.createUrlTree(['/login']);
        }

        // Admin has full access
        if (Common.isAdminUserLogin(this.storage)) {
            return true;
        }

        const userRole = (user.refTable || '').toString().trim().toLowerCase();
        
        const userId = user.refTableId;

        // Extract IDs from route parameters
        const id = route.params['id'];
        const studentId = route.params['studentId'] || route.params['student_id'];
        const staffId = route.params['staffId'] || route.params['staff_id'];
        const roomId = route.params['roomId'] || route.params['room_id'];

        // Determine the context based on the route path
        const url = state.url.toLowerCase();

        // Check if this is a staff profile route
        if (url.includes('/staff/staff-profile/') && id) {
            return this.validateStaffAccess(userRole, userId, +id);
        }

        // Check if this is a staff attendance profile route
        if (url.includes('/staff/viewattendance/staffattendanceprofile/') && id) {
            return this.validateStaffAccess(userRole, userId, +id);
        }

        // Check if this is a profile view route (attendance)
        if (url.includes('/staff/viewAttendance/profile-view/') && id) {
            return this.validateStaffAccess(userRole, userId, +id);
        }

        // Check if this is a student profile route
        if ((url.includes('/myschool/student/profile/') || url.includes('/student/profile/')) && id) {
            return this.validateStudentAccess(userRole, userId, +id);
        }

        // Check if this is a student attendance profile route
        if (url.includes('/student-sign-in/profileviewattendance/') && id) {
            return this.validateStudentAccess(userRole, userId, +id);
        }

        // Check if this is a room profile route
        if (url.includes('/myschool/room/room-profile/') && id) {
            return this.validateRoomAccess(userRole, userId, +id);
        }

        // Check if this is a family transaction route (for parents)
        if ((url.includes('/billing/accounting/family-transaction') ||
             url.includes('/billings/accounting/family-transaction') ||
             url.includes('/billing/accounting/familylayout/family-transaction') ||
             url.includes('/billing/accounting/familylayout/statement') ||
             url.includes('/billings/accounting/familylayout/family-transaction'))) {
                const userRole = (user.refTable || '').toString().trim().toLowerCase();

            // For new layout, ID comes from query params
            let studentId = id;
            if (!studentId) {
                // Extract from query params for new family layout
                studentId = route.queryParams['id'];
            }
            if (studentId) {
                return this.validateFamilyTransactionAccess(userRole, userId, +studentId);
            }
        }


        // Route-specific validation for other parameter names
        if (studentId) {
            return this.validateStudentAccess(userRole, userId, +studentId);
        }

        if (staffId) {
            return this.validateStaffAccess(userRole, userId, +staffId);
        }

        if (roomId) {
            return this.validateRoomAccess(userRole, userId, +roomId);
        }

        // If no specific ID parameters, allow access (for list views, etc.)
        return true;
    }

    private validateStudentAccess(userRole: string, userId: number, studentId: number): Observable<boolean | UrlTree> {
        if (userRole === 'staff') {
            // Staff can only access students in their assigned rooms
            return this.lookupService.getStudentById(studentId).pipe(
                switchMap((studentResponse: HttpResponse<StudentDTO>) => {
                    const student = studentResponse.body;
                    if (!student || !student.roomId) {
                        return of(this.router.createUrlTree(['/notfound']));
                    }

                    // Get staff details to check room assignments
                    return this.lookupService.queryStaff({ 'id.equals': userId }).pipe(
                        map((staffResponse: HttpResponse<StaffDTO[]>) => {
                            const staff = staffResponse.body?.[0];
                            if (!staff || !staff.rooms) {
                                return this.router.createUrlTree(['/notfound']);
                            }

                            const hasAccess = staff.rooms.some((room: any) => room.id === student.roomId);
                            return hasAccess || this.router.createUrlTree(['/notfound']);
                        }),
                        catchError(() => of(this.router.createUrlTree(['/notfound'])))
                    );
                }),
                catchError(() => of(this.router.createUrlTree(['/notfound'])))
            );
        } else if (userRole === 'parent' || userRole === 'guardian') {
            // Parents can only access their own children
            return this.lookupService.queryGuardian({ 'id.equals': userId }).pipe(
                switchMap((guardianResponse: HttpResponse<GuardianDTO[]>) => {
                    const guardian = guardianResponse.body?.[0];
                    if (!guardian) {
                        return of(this.router.createUrlTree(['/notfound']));
                    }

                    // Check if the student ID is in the guardian's children list
                    const hasAccess = guardian.students?.some((student: StudentDTO) => student.id === studentId) ||
                                    guardian.studentId === studentId;

                    return of(hasAccess || this.router.createUrlTree(['/notfound']));
                }),
                catchError(() => of(this.router.createUrlTree(['/notfound'])))
            );
        }

        return of(this.router.createUrlTree(['/notfound']));
    }

    private validateStaffAccess(userRole: string, userId: number, staffId: number): Observable<boolean | UrlTree> {
        if (userRole === 'staff') {
            // Staff can only access their own profile
            return of(userId === staffId || this.router.createUrlTree(['/notfound']));
        } else if (userRole === 'parent' || userRole === 'guardian') {
            // Parents cannot access staff profiles
            return of(this.router.createUrlTree(['/notfound']));
        }

        return of(this.router.createUrlTree(['/notfound']));
    }

    private validateRoomAccess(userRole: string, userId: number, roomId: number): Observable<boolean | UrlTree> {
        if (userRole === 'staff') {
            // Staff can only access their assigned rooms
            return this.lookupService.queryStaff({ 'id.equals': userId }).pipe(
                map((staffResponse: HttpResponse<StaffDTO[]>) => {
                    const staff = staffResponse.body?.[0];
                    if (!staff || !staff.rooms) {
                        return this.router.createUrlTree(['/notfound']);
                    }

                    const hasAccess = staff.rooms.some((room: any) => room.id === roomId);
                    return hasAccess || this.router.createUrlTree(['/notfound']);
                }),
                catchError(() => of(this.router.createUrlTree(['/notfound'])))
            );
        } else if (userRole === 'parent' || userRole === 'guardian') {
            // Parents cannot access room-specific routes
            return of(this.router.createUrlTree(['/notfound']));
        }

        return of(this.router.createUrlTree(['/notfound']));
    }

    private validateFamilyTransactionAccess(userRole: string, userId: number, studentId: number): Observable<boolean | UrlTree> {
        console.log('userRole', userRole);

        if (userRole === 'parent' || userRole === 'guardian') {
            // Parents can only access family transactions for their own children
            return this.lookupService.queryGuardian({ 'id.equals': userId }).pipe(
                switchMap((guardianResponse: HttpResponse<GuardianDTO[]>) => {
                    const guardian = guardianResponse.body?.[0];
                    if (!guardian) {
                        return of(this.router.createUrlTree(['/notfound']));
                    }

                    // Check if the student ID is in the guardian's children list
                    const hasAccess = guardian.students?.some((student: StudentDTO) => student.id === studentId) ||
                                    guardian.studentId === studentId;

                    return of(hasAccess || this.router.createUrlTree(['/notfound']));
                }),
                catchError(() => of(this.router.createUrlTree(['/notfound'])))
            );
        } else if (userRole === 'staff') {
            // Staff cannot access family transaction routes
            return of(this.router.createUrlTree(['/notfound']));
        } else if (Common.isAdminUserLogin(this.storage)) {
            // Admins can access family transactions for students in their school
            // The school guard will handle the school validation
            return of(true);
        }

        return of(this.router.createUrlTree(['/notfound']));
    }
}